import { useRef, useState } from 'react';
import { UploadSimple, File, X } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import styles from './FileUploadField.module.css';

interface FileUploadFieldProps {
    value: string;
    setValue: (value: string) => void;
    error?: string;
    maxSizeMb?: number;
    allowedTypes?: string[];
    onFileSelected?: (file: File) => Promise<string>;
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadField({
    value,
    setValue,
    error,
    maxSizeMb = 10,
    allowedTypes = [],
    onFileSelected,
}: FileUploadFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileName, setFileName] = useState('');

    const validateFile = (file: File): string | null => {
        if (file.size > maxSizeMb * 1024 * 1024) {
            return `El archivo supera el tamano maximo de ${maxSizeMb}MB`;
        }
        if (allowedTypes.length > 0) {
            const matches = allowedTypes.some((t) => {
                if (t.endsWith('/*')) {
                    return file.type.startsWith(t.slice(0, -2));
                }
                return file.type === t;
            });
            if (!matches) {
                return `Tipo de archivo no permitido. Formatos aceptados: ${allowedTypes.join(', ')}`;
            }
        }
        return null;
    };

    const processFile = async (file: File) => {
        const validationError = validateFile(file);
        if (validationError) {
            toast.error(validationError);
            return;
        }

        setFileName(file.name);

        if (onFileSelected) {
            setUploading(true);
            try {
                const url = await onFileSelected(file);
                setValue(url);
            } catch {
                toast.error('No se pudo subir el archivo');
                setFileName('');
            } finally {
                setUploading(false);
            }
        } else {
            // Fallback: store file name in value (dev mode)
            setValue(file.name);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
    };

    const handleClear = () => {
        setValue('');
        setFileName('');
        if (inputRef.current) inputRef.current.value = '';
    };

    const displayName = fileName || (value ? value.split('/').pop() : '');

    return (
        <div className={styles.wrapper}>
            {!value ? (
                <div
                    className={`${styles.dropZone} ${isDragging ? styles.dragging : ''} ${error ? styles.errorZone : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => inputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
                    aria-label="Subir archivo"
                >
                    {uploading ? (
                        <div className={styles.uploadingState}>
                            <div className={styles.spinner} aria-hidden="true" />
                            <span className={styles.uploadingText}>Subiendo...</span>
                        </div>
                    ) : (
                        <>
                            <UploadSimple size={28} className={styles.uploadIcon} />
                            <p className={styles.dropText}>
                                Arrastra un archivo aqui o <span className={styles.browseLink}>selecciona</span>
                            </p>
                            <p className={styles.hint}>
                                {allowedTypes.length > 0 && `Tipos: ${allowedTypes.join(', ')} · `}
                                Maximo {maxSizeMb}MB
                            </p>
                        </>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        className={styles.hiddenInput}
                        onChange={handleFileChange}
                        accept={allowedTypes.join(',')}
                        aria-hidden="true"
                    />
                </div>
            ) : (
                <div className={styles.previewRow}>
                    <File size={20} className={styles.fileIcon} />
                    <span className={styles.previewName}>{displayName}</span>
                    <button
                        type="button"
                        className={styles.clearBtn}
                        onClick={handleClear}
                        aria-label="Quitar archivo"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
            {error && <span className={styles.errorMsg} role="alert">{error}</span>}
        </div>
    );
}

// Re-export with size formatter for external usage
FileUploadField.formatFileSize = formatFileSize;
